import React, { useEffect, useRef, useState } from 'react';
import { useData } from '../DataContext';

// In-app notifications on job completion / stall. Uses the browser Notification
// API when permitted, plus an always-visible in-app toast fallback. Intentionally
// in-app only — emailing job data would be an external transfer of controlled
// technical data (an Export Compliance decision), so it's not done here.
const STALL_SEC = 300;

export default function NotificationWatcher() {
  const { clusters } = useData();
  const [toasts, setToasts] = useState([]);
  const prevCompleted = useRef(null);
  const notifiedStall = useRef(new Set());
  const permAsked = useRef(false);

  useEffect(() => {
    if (!permAsked.current && 'Notification' in window && Notification.permission === 'default') {
      permAsked.current = true;
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const running = [];
    const completed = [];
    Object.values(clusters || {}).forEach((c) => {
      (c.running || []).forEach((j) => running.push(j));
      (c.completed || []).forEach((j) => completed.push(j));
    });
    const completedNames = new Set(completed.map((j) => j.jobName).filter(Boolean));
    const runningNames = new Set(running.map((j) => j.jobName).filter(Boolean));

    const fire = (title, body, kind) => {
      const id = `${Date.now()}-${Math.random()}`;
      setToasts((t) => [...t.slice(-4), { id, title, body, kind }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 9000);
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(title, { body });
        }
      } catch { /* ignore */ }
    };

    // Completions: names newly appearing in the completed list. Baseline on the
    // first snapshot so we don't fire for already-finished jobs.
    if (prevCompleted.current === null) {
      prevCompleted.current = completedNames;
    } else {
      completedNames.forEach((n) => {
        if (!prevCompleted.current.has(n)) fire('Job completed', n, 'done');
      });
      prevCompleted.current = completedNames;
    }

    // Stalls: running job with no output for > threshold (fire once per job).
    const now = Date.now() / 1000;
    running.forEach((j) => {
      const age = j.lastActivityTs ? now - j.lastActivityTs : 0;
      if (j.lastActivityTs && age > STALL_SEC) {
        if (!notifiedStall.current.has(j.jobName)) {
          notifiedStall.current.add(j.jobName);
          fire('Job stalled', `${j.jobName} — no output for ${Math.floor(age / 60)}m`, 'stall');
        }
      } else if (notifiedStall.current.has(j.jobName)) {
        notifiedStall.current.delete(j.jobName); // recovered
      }
    });
    [...notifiedStall.current].forEach((n) => { if (!runningNames.has(n)) notifiedStall.current.delete(n); });
  }, [clusters]);

  if (toasts.length === 0) return null;
  return (
    <div className="no-print" style={{
      position: 'fixed', top: 16, right: 16, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 340,
    }}>
      {toasts.map((t) => {
        const c = t.kind === 'stall' ? '#ef4444' : '#22c55e';
        return (
          <div key={t.id} style={{
            background: '#0f172a', border: `1px solid ${c}`, borderLeft: `4px solid ${c}`,
            borderRadius: 8, padding: '10px 12px', boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
          }}>
            <div style={{ color: t.kind === 'stall' ? '#f87171' : '#4ade80', fontWeight: 700, fontSize: 12, marginBottom: 2 }}>{t.title}</div>
            <div style={{ color: '#d1d5db', fontSize: 11, wordBreak: 'break-word', fontFamily: 'monospace' }}>{t.body}</div>
          </div>
        );
      })}
    </div>
  );
}
